package com.evoapps.evoshift.domain.staffimport

/*
 * SH-214: importing the staff list from a spreadsheet. Any layout is accepted through an explicit
 * column mapping (owner's decision, 2026-10-21): the headers are matched to our fields in Polish,
 * English and Ukrainian ([StaffHeaders]), the person checks or changes each column, then sees every
 * row validated ([StaffImportValidator]) before anything is saved (ImportEmployeeUseCase, the same
 * path as adding a person by hand). See docs/user/<lang>/team-and-invitations.md#import.
 */

/** What a column of the file holds. */
enum class StaffImportField(
    /**
     * Pay terms and contact details, saved to the private `users/{uid}/sensitive` documents: only
     * offered to those who may write them (Capability.MANAGE_PAY).
     */
    val isPrivate: Boolean = false
) {
    /** The whole name ("Jan Kowalski"). */
    NAME,
    /** First name and surname in separate columns: joined into the name. */
    FIRST_NAME,
    LAST_NAME,
    /** For the invitation (and kept with the private data). */
    EMAIL,
    PHONE(isPrivate = true),
    /** The position (stanowisko), e.g. "Barista". */
    ROLE,
    /** Employee, manager or manager with finances; employee when empty. */
    PERMISSION,
    CONTRACT(isPrivate = true),
    /** Hourly rate or monthly salary; hourly when empty. */
    PAY_MODEL(isPrivate = true),
    /** The hourly rate or the monthly salary, by [PAY_MODEL]. */
    RATE(isPrivate = true),
    /** Pupil or student under 26 (no contributions on a civil-law contract). */
    STUDENT(isPrivate = true),
    /** Leads shifts: gets the workplace's shift lead skill. */
    SHIFT_LEAD;

    companion object {
        /** The fields offered to someone who may ([withPrivate]) or may not write pay and contact details. */
        fun offered(withPrivate: Boolean): List<StaffImportField> = entries.filter { withPrivate || !it.isPrivate }
    }
}

/** Header names of each field (PL, EN, UK), compared [normalised][StaffHeaders.normalise]. */
object StaffHeaders {

    private val SYNONYMS: Map<StaffImportField, List<String>> = mapOf(
        StaffImportField.NAME to listOf(
            "Imię i nazwisko", "Imie nazwisko", "Nazwisko i imię", "Pracownik", "Pracownica", "Osoba", "Imię i nazwisko pracownika",
            "Name", "Full name", "Employee", "Employee name", "Staff", "Person",
            "Ім'я та прізвище", "Прізвище та ім'я", "Працівник", "ПІБ"
        ),
        StaffImportField.FIRST_NAME to listOf("Imię", "First name", "Given name", "Forename", "Ім'я"),
        StaffImportField.LAST_NAME to listOf("Nazwisko", "Last name", "Surname", "Family name", "Прізвище"),
        StaffImportField.EMAIL to listOf("E-mail", "Email", "Mail", "Adres e-mail", "Adres email", "E-mail address", "Email address", "Електронна адреса", "Електронна пошта", "Пошта"),
        StaffImportField.PHONE to listOf(
            "Telefon", "Tel", "Tel.", "Nr telefonu", "Numer telefonu", "Telefon komórkowy", "Komórka",
            "Phone", "Phone number", "Mobile", "Mobile phone", "Телефон", "Номер телефону"
        ),
        StaffImportField.ROLE to listOf("Stanowisko", "Rola", "Funkcja", "Role", "Position", "Job title", "Job", "Посада", "Роль"),
        StaffImportField.PERMISSION to listOf("Uprawnienia", "Uprawnienie", "Dostęp", "Permission", "Permissions", "Access", "Права доступу", "Доступ"),
        StaffImportField.CONTRACT to listOf("Umowa", "Rodzaj umowy", "Typ umowy", "Forma zatrudnienia", "Contract", "Contract type", "Договір", "Тип договору"),
        StaffImportField.PAY_MODEL to listOf(
            "Forma wynagrodzenia", "Model wynagrodzenia", "Rodzaj wynagrodzenia", "Rodzaj stawki",
            "Pay model", "Pay type", "Compensation", "Форма оплати", "Тип оплати"
        ),
        StaffImportField.RATE to listOf(
            "Stawka", "Stawka godzinowa", "Stawka (zł/h)", "Wynagrodzenie", "Pensja", "Kwota",
            "Rate", "Hourly rate", "Pay rate", "Pay", "Salary", "Wage", "Ставка", "Зарплата", "Оплата"
        ),
        StaffImportField.STUDENT to listOf("Student", "Uczeń", "Student lub uczeń", "Status studenta", "Uczeń lub student", "Студент"),
        StaffImportField.SHIFT_LEAD to listOf(
            "Lider zmiany", "Kierownik zmiany", "Prowadzi zmianę", "Zmianowy", "Shift lead", "Shift leader", "Leads shifts",
            "Старший зміни", "Керівник зміни"
        )
    )

    private val NORMALISED: List<Pair<String, StaffImportField>> =
        SYNONYMS.flatMap { (field, names) -> names.map { normalise(it) to field } }.sortedByDescending { it.first.length }

    /**
     * The field a header names: an exact synonym, else the longest synonym the header starts with
     * ("Stawka brutto" is a rate, "Imię i nazwisko pracownika" a name); null: not recognised.
     */
    fun fieldOf(header: String): StaffImportField? {
        val name = normalise(header)
        if (name.isEmpty()) return null
        NORMALISED.firstOrNull { it.first == name }?.let { return it.second }
        return NORMALISED.firstOrNull { (synonym, _) -> synonym.length >= 4 && name.startsWith(synonym) }?.second
    }

    /** Lower case, Polish letters without their marks, letters (any alphabet) and digits only. */
    fun normalise(text: String): String = buildString {
        for (c in text.lowercase()) {
            val plain = when (c) {
                'ą' -> 'a'; 'ć' -> 'c'; 'ę' -> 'e'; 'ł' -> 'l'; 'ń' -> 'n'; 'ó' -> 'o'; 'ś' -> 's'; 'ź', 'ż' -> 'z'
                else -> c
            }
            if (plain.isLetterOrDigit()) append(plain)
        }
    }
}

/**
 * Which column holds which field. [headerRow]: the 0-based row with the column names (null: the
 * file has none and every row is a person). [fields]: per column, null when it is not imported.
 */
data class StaffColumnMapping(val headerRow: Int?, val fields: List<StaffImportField?>) {
    /** The name comes from a name column, or from first name and surname columns. */
    val hasName: Boolean
        get() = StaffImportField.NAME in fields || StaffImportField.FIRST_NAME in fields || StaffImportField.LAST_NAME in fields

    fun columnOf(field: StaffImportField): Int? = fields.indexOf(field).takeIf { it >= 0 }

    /** [column] now holds [field] (null: skipped); another column that held it no longer does. */
    fun with(column: Int, field: StaffImportField?): StaffColumnMapping {
        val updated = fields.toMutableList()
        while (updated.size <= column) updated += null
        if (field != null) updated.indices.forEach { if (updated[it] == field) updated[it] = null }
        updated[column] = field
        return copy(fields = updated)
    }
}

/** One person's row: its row number in the spreadsheet (1-based) and its mapped cells, trimmed. */
data class StaffImportRow(val number: Int, val values: Map<StaffImportField, String>) {
    operator fun get(field: StaffImportField): String = values[field].orEmpty()
}

object StaffImportMapping {

    /**
     * The mapping to start from: the first non-blank row is the header when one of its cells names
     * a field ([StaffHeaders]); then each recognised column maps to its field (the first column
     * wins when two name the same one). Without a header the columns are guessed from their
     * content: e-mail addresses, phone numbers, and the first text column as the name. Only the
     * [offered] fields are used.
     */
    fun suggest(rows: List<List<String>>, offered: Collection<StaffImportField>): StaffColumnMapping {
        val first = rows.indexOfFirst { row -> row.any { it.isNotBlank() } }
        if (first < 0) return StaffColumnMapping(null, emptyList())
        val width = rows.maxOf { it.size }
        val fromHeader = headerFields(rows[first], width, offered)
        return if (fromHeader.any { it != null }) StaffColumnMapping(first, fromHeader)
        else StaffColumnMapping(null, guessFromContent(rows.drop(first), width, offered))
    }

    /** The fields the header cells name (each field once; a surname without a first name is the name). */
    fun headerFields(header: List<String>, width: Int, offered: Collection<StaffImportField>): List<StaffImportField?> {
        val fields = MutableList<StaffImportField?>(width) { null }
        for (column in 0 until width) {
            val field = StaffHeaders.fieldOf(header.getOrNull(column).orEmpty())?.takeIf { it in offered } ?: continue
            if (field !in fields) fields[column] = field
        }
        if (StaffImportField.NAME !in fields && StaffImportField.FIRST_NAME !in fields) {
            val surname = fields.indexOf(StaffImportField.LAST_NAME)
            if (surname >= 0) fields[surname] = StaffImportField.NAME
        }
        return fields
    }

    /** Without names: e-mails, phones (when offered) and the first text column as the name. */
    fun guessFromContent(rows: List<List<String>>, width: Int, offered: Collection<StaffImportField>): List<StaffImportField?> {
        val fields = MutableList<StaffImportField?>(width) { null }
        val sample = rows.filter { row -> row.any { it.isNotBlank() } }.take(20)
        fun cells(column: Int) = sample.mapNotNull { it.getOrNull(column)?.trim()?.takeIf(String::isNotEmpty) }
        fun mostly(column: Int, test: (String) -> Boolean): Boolean {
            val values = cells(column)
            return values.isNotEmpty() && values.count(test) * 2 > values.size
        }
        for (column in 0 until width) {
            when {
                StaffImportField.EMAIL in offered && StaffImportField.EMAIL !in fields && mostly(column) { '@' in it } -> fields[column] = StaffImportField.EMAIL
                StaffImportField.PHONE in offered && StaffImportField.PHONE !in fields && mostly(column) { StaffValues.phone(it, null) != null && it.count(Char::isDigit) >= 9 } ->
                    fields[column] = StaffImportField.PHONE
            }
        }
        val name = (0 until width).firstOrNull { column ->
            fields[column] == null && mostly(column) { value -> value.any { it.isLetter() } && '@' !in value }
        }
        if (name != null) fields[name] = StaffImportField.NAME
        return fields
    }

    /** The people's rows under [mapping]: after the header, blank rows left out. */
    fun rows(rows: List<List<String>>, mapping: StaffColumnMapping): List<StaffImportRow> {
        val start = (mapping.headerRow ?: -1) + 1
        return (start until rows.size).mapNotNull { index ->
            val row = rows[index]
            val values = buildMap {
                mapping.fields.forEachIndexed { column, field ->
                    if (field != null) put(field, row.getOrNull(column).orEmpty().trim())
                }
            }
            if (values.values.all { it.isEmpty() }) null else StaffImportRow(index + 1, values)
        }
    }
}
